import './notification-item.scss';
import React, { useMemo } from 'react';
import moment from 'moment';
import PolicyNotification from './notification-item-components/policy-notification';
import LogNotification from './notification-item-components/log-notification';
import {
    MlTipNotificationDto,
    NotificationDto,
    NotificationResponseStatus,
    NotificationType,
    PolicyNotificationDto,
    TipNotificationDto
} from '@src/dtos/notification.dto';
import PendingIcon from '@svg/clock-filled-16.svg?react';
import SuccessIcon from '@svg/green-checkmark-circle.svg?react';
import FailureIcon from '@svg/x-circle-red.svg?react';
import PolicyPartialIcon from '@svg/warn-circle.svg?react';
import AIIcon from '@svg/ai.svg?react';
import TipNotification from './notification-item-components/tip-notification';
import MlTipNotification from './notification-item-components/ml-tip-notification';
import { PolicyType } from '@src/utils/enums';

interface NotificationItemProps {
    notification: NotificationDto;
    isNew: boolean;
    serviceName?: string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, isNew, serviceName }) => {

    const isFailure = useMemo(() => notification.status === NotificationResponseStatus.NOTIFICATION_ERROR, [notification]);

    const getStatusIcon = (notification: NotificationDto) => {
        switch (notification.status) {
            case NotificationResponseStatus.NOTIFICATION_PENDING:
                return PendingIcon;
            case NotificationResponseStatus.NOTIFICATION_SUCCESS:
                return SuccessIcon;
            case NotificationResponseStatus.NOTIFICATION_ERROR:
                return FailureIcon;
            case NotificationResponseStatus.NOTIFICATION_PARTIAL_SUCCESS:
                return PolicyPartialIcon;
            default:
                return null;
        }
    };

    const getPolicyNotificationHeader = (notification: PolicyNotificationDto) => {

        const versionType = notification.versionType ?? PolicyType.DEFAULT;

        if ([PolicyType.DEACTIVATE, PolicyType.REACTIVATE].includes(versionType)) {
            const versionTypeText = versionType.toLowerCase();
            const versionTypeTextCapitalized = versionTypeText.charAt(0).toUpperCase() + versionTypeText.slice(1);
            switch (notification.status) {
                case NotificationResponseStatus.NOTIFICATION_PENDING:
                    return `${versionTypeTextCapitalized} policy in process`;
                case NotificationResponseStatus.NOTIFICATION_SUCCESS:
                    return `Policy successfully ${versionTypeText}d`;
                case NotificationResponseStatus.NOTIFICATION_ERROR:
                    return `${versionTypeTextCapitalized} policy failed`;
            }
        }
        else {
            const policyTitle = notification.pushPolicyTargetConnectionNameAndStatusList ? 'Copy policy' : 'Policy';

            switch (notification.status) {
                case NotificationResponseStatus.NOTIFICATION_PENDING:
                    return `${policyTitle} submission in process`;
                case NotificationResponseStatus.NOTIFICATION_SUCCESS:
                    return `${policyTitle} applied successfully`;
                case NotificationResponseStatus.NOTIFICATION_ERROR:
                    return `${policyTitle} submission failed`;
                case NotificationResponseStatus.NOTIFICATION_PARTIAL_SUCCESS:
                    return `${policyTitle} partially applied`;
            }
        }
    };

    const header = useMemo(() => {
        if (notification.type === NotificationType.TIPS) {
            switch ((notification as TipNotificationDto).tipNotificationType) {
                case 'appRecommendation':
                    return 'Policy update recommendation';
                case 'mlRecommendation':
                    return <>Policy update recommendation<AIIcon/></>;
            }
        }

        if (notification.type === NotificationType.POLICY_UPDATE) {
            const StatusIcon = getStatusIcon(notification);

            return <>
                {getPolicyNotificationHeader(notification as PolicyNotificationDto)}
                {StatusIcon && <StatusIcon className="notification-item-header-status-icon" data-testid="policy-notification-status-icon" />}
            </>;
        }

        const StatusIcon = getStatusIcon(notification);
        switch (notification.status) {
            case NotificationResponseStatus.NOTIFICATION_PENDING:
                return <>Log export in process{StatusIcon && <StatusIcon />}</>;
            case NotificationResponseStatus.NOTIFICATION_SUCCESS:
                return <>Log export completed{StatusIcon && <StatusIcon />}</>;
            case NotificationResponseStatus.NOTIFICATION_ERROR:
                return <>Logs has failed to export{StatusIcon && <StatusIcon />}</>;
        }
    }, [notification]);

    return (
        <div className={`notification-item ${isNew ? 'new-notification' : ''}`}>
            <div className="notification-item-header">
                <span className="notification-item-header-title">
                  {header}
                </span>
                <span className="notification-item-header-date">
                    <span>{moment.utc(notification.notificationTimestamp).local().format('MMMM D H:mm')}</span>
                </span>
            </div>
            {serviceName && <span className="notification-service-name">{serviceName}</span>}
            {notification.type === NotificationType.POLICY_UPDATE &&
                <PolicyNotification notification={notification as PolicyNotificationDto} isFailure={isFailure}/>}
            {notification.type === NotificationType.LOG_EXPORT && <LogNotification notification={notification} isFailure={isFailure}/>}
            {notification.type === NotificationType.TIPS && (
                (notification as TipNotificationDto).tipNotificationType === 'mlRecommendation'
                    ? <MlTipNotification notification={notification as MlTipNotificationDto} serviceName={serviceName}/>
                    : <TipNotification notification={notification}/>
            )}
        </div>
    );
};

export default NotificationItem;
